import { useCallback } from 'react';
import { useParams } from 'react-router';

import { useAppDispatch, useAppSelector } from 'js/store/hooks';
import { VSLibraryImage, PremiumSearchImage, ImageLibrarySearch, ImageLibraryCategory, ImageSource } from 'js/types';
import { getCategoryImages, getSearchImages } from 'js/actionCreators/ImageDrawerActions';
import { trackImageSearch } from 'js/actionCreators/trackingActions';
import { SearchType } from 'js/reducers/imageSearchReducer';
import { SEARCH_MODE_CONFIG } from 'js/editor/ImageDrawer/imageSearchModeConfig';

import useGetSearchResult from './useGetSearchResult';

type ExistingTabResult =
  | ImageLibrarySearch<VSLibraryImage>
  | ImageLibrarySearch<PremiumSearchImage>
  | ImageLibraryCategory;

const useLoadImages = (
  searchType: SearchType,
  searchInput: string,
  categoryId: string | null,
  selectedTab: ImageSource
) => {
  const dispatch = useAppDispatch();
  const { scribeId } = useParams<{ scribeId: string }>();
  const activeSearchMode = useAppSelector(state => state.imageSearch.activeSearchMode);

  const { getExistingTabResult, isTabLoading } = useGetSearchResult(searchType, searchInput, categoryId);

  const isSearchData = (
    data: ExistingTabResult | undefined
  ): data is ImageLibrarySearch<VSLibraryImage> | ImageLibrarySearch<PremiumSearchImage> => {
    return !data || 'offset' in data;
  };

  const fetchImages = useCallback(
    (tab: ImageSource, initFetch = false, isPrefetch = false) => {
      if (isTabLoading(tab)) return;
      if (searchType === SearchType.INPUT && tab === 'streamline-graphics' && !searchInput) {
        console.warn('Streamline graphics search requires a search input');
        return;
      }

      const rawData = getExistingTabResult(tab);
      // Treat entries with no images yet (e.g. only {isViewed: true}) as uninitialized
      const selectedData = rawData && 'images' in rawData ? rawData : undefined;
      if (searchType === SearchType.INPUT && isSearchData(selectedData)) {
        const { total, offset, pagination } = selectedData ?? {};

        // If pagination array exists, use it as the source of truth
        if (pagination && pagination.length > 0) {
          const isNoMorePagination = pagination.every(p => p.hasMore === false);
          if (isNoMorePagination) return;
        } else {
          // Fall back to total/offset check only when pagination is not available
          const isNoMoreResults = total !== undefined && offset !== undefined && total <= offset;
          if (isNoMoreResults) return;
        }

        dispatch(
          getSearchImages(tab, searchInput, selectedData?.offset ?? 0, selectedData?.nextPageId, isPrefetch, pagination)
        );
        if (initFetch && !isPrefetch) dispatch(trackImageSearch(searchInput, parseInt(scribeId), tab));
      } else if (searchType === SearchType.CATEGORY && categoryId) {
        // If pagination array exists, use it as the source of truth
        if (selectedData?.pagination && selectedData.pagination.length > 0) {
          const isNoMorePagination = selectedData.pagination.every(p => p.hasMore === false);
          if (isNoMorePagination) return;
        } else {
          // Fall back to nextPageId check only when pagination is not available
          if (selectedData?.nextPageId === null) return;
        }
        dispatch(getCategoryImages(categoryId, tab, selectedData?.nextPageId ?? '0', selectedData?.pagination));
      }
    },
    [categoryId, dispatch, getExistingTabResult, searchInput, searchType, isTabLoading, scribeId]
  );

  const loadImages = useCallback(
    (initFetch = false) => {
      fetchImages(selectedTab, initFetch, false);
      if (initFetch) {
        const { excludedTabs } = SEARCH_MODE_CONFIG[activeSearchMode];
        const alternativeTabs = (['videoscribe'] as ImageSource[]).filter(
          tab => tab !== selectedTab && !excludedTabs.includes(tab)
        );
        alternativeTabs.forEach(tab => fetchImages(tab, initFetch, true));
      }
    },
    [selectedTab, fetchImages, activeSearchMode]
  );

  return loadImages;
};

export default useLoadImages;
